import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, ArrowRight, CheckCircle2, RefreshCw, LockKeyhole, Lock, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { useAgenteAtualStore } from "@/stores/agenteAtualStore";
import { agenteService } from "@/services/agenteService";
import { autenticacaoService } from "@/services/autenticacaoService";
import { decryptToken } from "@/utils/tokenCrypto";
import { AppConfig } from "@/datasources/AppConfig";
import logoUrbnet from "@/assets/images/UrbNetLogoSemFundo.png";

const FloatingOrb = ({ className, delay = 0 }: { className?: string; delay?: number }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl opacity-30 ${className}`}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      scale: [1, 1.1, 1],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

export default function AutenticacaoDoisFatoresPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fetchAgenteAtual = useAgenteAtualStore((s) => s.fetchAgenteAtual);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = verificar código, 2 = definir senha
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [canResend, setCanResend] = useState(false);
  const [userId, setUserId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [mode, setMode] = useState("");
  const [activateEmail, setActivateEmail] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const isPasswordStep = step === 2;
  const [formData, setFormData] = useState({
    senha: '',
    confirmarSenha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [verifiedCode, setVerifiedCode] = useState("");

  // Função para atualizar foto de perfil com imagem do Google
  const atualizarFotoGoogle = async () => {
    const googlePictureUrl = localStorage.getItem('googlePictureUrl');
    if (!googlePictureUrl) return;

    try {
      const agente = agenteService.getAgenteCache();
      if (!agente?.id) return;

      const imageFile = await agenteService.baixarImagemComoFile(googlePictureUrl, 'google-profile.jpg');
      if (!imageFile) {
        return;
      }

      const resultado = await agenteService.atualizarFotoPerfil(agente.id, imageFile);
      if ('success' in resultado) {
        localStorage.removeItem('googlePictureUrl');
      }
    } catch (err) {
      // Error updating Google profile photo
    }
  };

  const passwordRules = [
    { label: 'Mínimo 6 caracteres', valid: formData.senha.length >= 6 },
    { label: 'Pelo menos 1 letra maiúscula (A-Z)', valid: /[A-Z]/.test(formData.senha) },
    { label: 'Pelo menos 1 letra minúscula (a-z)', valid: /[a-z]/.test(formData.senha) },
    { label: 'Pelo menos 1 número (0-9)', valid: /\d/.test(formData.senha) },
    { label: 'Pelo menos 1 caractere especial (!@#$...)', valid: /[^A-Za-z0-9]/.test(formData.senha) },
  ];

  // Get userId and userEmail from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedData = params.get("d");

    if (encryptedData) {
      // Descriptografar dados do parâmetro "d"
      decryptToken(encryptedData).then((decrypted) => {
        try {
          const data = JSON.parse(decrypted);
          if (data.usuarioId) setUserId(data.usuarioId);
          if (data.email) setUserEmail(data.email);
          if (data.tokenAcesso) {
            // Salvar token temporariamente para usar na ativação
            localStorage.setItem('tempTokenAcesso', data.tokenAcesso);
          }
        } catch (e) {
          // Error decrypting data
        }
      });
    } else {
      // Fallback para parâmetros antigos (guid, email, mode)
      const id = params.get("guid") || "";
      const email = params.get("email") || "";
      const mode = params.get("mode") || "";
      setUserId(id);
      setUserEmail(email);
      setMode(mode);
    }
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    }

    // Contador chegou a 0: habilita o botão manual e dispara o reenvio automático.
    // Em caso de sucesso, handleResendCode reinicia o contador (120s), criando o ciclo.
    setCanResend(true);
    const podeReenviar = step === 1 && !!userId && !!(userEmail || activateEmail);
    if (podeReenviar) {
      handleResendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    const newCode = [...code];
    
    pastedData.split("").forEach((char, index) => {
      if (index < 6) {
        newCode[index] = char;
      }
    });
    
    setCode(newCode);
  };

  const handleResendCode = async () => {
    // Usar endpoint esqueceu-senha que não requer autenticação
    // POST /autenticacao/v1/usuarios/esqueceu-senha
    // body: { id: e-mail ou telefone }
    const identificador = (userEmail || activateEmail || "").trim();

    if (!identificador) {
      toast({
        title: "E-mail necessário",
        description: "Não foi possível identificar o e-mail/telefone para reenviar o código.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlSecondary);

      const result = await dataSource.post({
        endpoint: 'v1/usuarios/esqueceu-senha',
        requiredToken: false,
        body: {
          id: identificador,
        },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao reenviar",
          description: result.failure.messages.join(", ") || "Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      setTimeLeft(120);
      setCanResend(false);
      setCode(["", "", "", "", "", ""]);

      toast({
        title: "Código reenviado",
        description: `Novo código enviado para ${identificador}.`,
      });
    } catch (error) {
      toast({
        title: "Erro ao reenviar",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!activateEmail || !activateEmail.includes('@')) {
      toast({
        title: "E-mail inválido",
        description: "Insira um e-mail válido.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlSecondary);

      // Usar endpoint esqueceu-senha para enviar código
      const result = await dataSource.post({
        endpoint: 'v1/usuarios/solicitar-codigo',
        requiredToken: false,
        body: {
          id: activateEmail
        },
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao enviar código",
          description: result.failure.message,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Código enviado com sucesso
      setCodeSent(true);
      setUserEmail(activateEmail);
      setTimeLeft(120);
      setCanResend(false);

      toast({
        title: "Código enviado!",
        description: "Enviamos um código para seu e-mail.",
      });
    } catch (error) {
      toast({
        title: "Erro ao enviar código",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const codeValue = code.join("");
    if (codeValue.length !== 6) {
      toast({
        title: "Código incompleto",
        description: "Digite os 6 dígitos do código.",
        variant: "destructive",
      });
      return;
    }

    // Modo activate: verificar código com email e redirecionar para login com modal
    if (mode === "activate") {
      if (!userEmail) {
        toast({
          title: "Dados faltando",
          description: "E-mail não encontrado. Volte e tente novamente.",
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);
      try {
        const dataSource = new RemoteDataSource(Settings.urlSecondary);

        // Usar email no endpoint de verificar código
        const result = await dataSource.post({
          endpoint: `v1/usuarios/${userEmail}/verificar-codigo`,
          requiredToken: true,
          body: {
            id: userEmail,
            codigo: codeValue
          },
        });

        if ('failure' in result) {
          toast({
            title: "Erro ao verificar código",
            description: result.failure.message,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Código verificado com sucesso, redirecionar para login com modal de ativação
        toast({
          title: "Código verificado!",
          description: "Agora defina sua senha para ativar a conta.",
        });

        // Salvar email e código no localStorage para usar no modal de ativação
        localStorage.setItem('activateEmail', userEmail);
        localStorage.setItem('activateCode', codeValue);

        setTimeout(() => {
          navigate('/login?activate=true');
        }, 1000);
      } catch (error) {
        toast({
          title: "Erro ao verificar código",
          description: "Ocorreu um erro inesperado. Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Novo fluxo com dados encriptados: usar token temporário e userId (GUID)
    const tempToken = localStorage.getItem('tempTokenAcesso');
    if (tempToken && userId) {
      setIsLoading(true);
      try {
        const dataSource = new RemoteDataSource(Settings.urlSecondary);
        Settings.token = { accessToken: tempToken };
        AppConfig.tokenAcesso = tempToken;

        // Verificar código usando userId (GUID) no path
        const result = await dataSource.post({
          endpoint: `v1/usuarios/${userId}/verificar-codigo`,
          requiredToken: true,
          body: {
            id: userEmail || userId,
            codigo: codeValue
          },
        });

        if ('failure' in result) {
          toast({
            title: "Erro ao verificar código",
            description: result.failure.message,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Código verificado com sucesso, avançar para definição de senha
        toast({
          title: "Código verificado!",
          description: "Agora defina sua senha para ativar a conta.",
        });

        setVerifiedCode(codeValue);
        setStep(2);
      } catch (error) {
        toast({
          title: "Erro ao verificar código",
          description: "Ocorreu um erro inesperado. Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // O código é validado junto com a ativação da conta no próximo passo
    // (evita que o código seja consumido duas vezes)
    setVerifiedCode(codeValue);
    setStep(2);
  }, [code, userId, userEmail, mode, navigate, toast]);

  // Auto-submit when all 6 code slots are filled
  useEffect(() => {
    const codeValue = code.join("");
    if (codeValue.length === 6 && step === 1 && !isLoading) {
      const timer = setTimeout(() => {
        handleVerifyCode(new Event("submit") as unknown as React.FormEvent);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [code, step, isLoading, handleVerifyCode]);

  const validatePasswordForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 6) {
      newErrors.senha = 'Senha deve ter pelo menos 6 caracteres';
    } else if (!/[A-Z]/.test(formData.senha) || !/[a-z]/.test(formData.senha) || !/\d/.test(formData.senha) || !/[^A-Za-z0-9]/.test(formData.senha)) {
      newErrors.senha = 'Inclua ao menos 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial';
    }

    if (!formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (formData.senha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleActivateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    // Sempre usar userId (GUID) no path parameter
    if (!userId || !verifiedCode) {
      toast({
        title: 'Dados faltando',
        description: 'Identificação do usuário ou código não encontrados. Volte e tente novamente.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const dataSource = new RemoteDataSource(Settings.urlSecondary);

      // Usar token temporário se disponível (do link encriptado)
      const tempToken = localStorage.getItem('tempTokenAcesso');
      if (tempToken) {
        Settings.token = { accessToken: tempToken };
        AppConfig.tokenAcesso = tempToken;
      }

      const result = await dataSource.post({
        endpoint: `v1/usuarios/${userId}/ativar-conta`,
        requiredToken: true,
        body: {
          id: userEmail || userId,
          codigo: verifiedCode,
          definirSenha: {
            senha: formData.senha,
            confirmacaoSenha: formData.confirmarSenha
          }
        },
      });

      // Salvar o token retornado pela ativação da conta
      if ('success' in result && result.success.body) {
        const tokenData = result.success.body as {
          tokenAcesso: string;
          tokenAtualizacao: string;
          emitido: string;
          expira: string;
          usuario: { id: string; nome: string; email?: string; telefone?: string; confirmado: boolean; foto?: string };
        };

        if (tokenData.tokenAcesso && tokenData.tokenAtualizacao) {
          await autenticacaoService.salvarToken(tokenData);
        }
      }

      toast({
        title: 'Conta ativada com sucesso!',
        description: 'Sua conta foi criada e ativada com sucesso.',
      });

      // Clear the verification code from localStorage
      localStorage.removeItem('verificationCode');
      localStorage.removeItem('userId');
      localStorage.removeItem('tempTokenAcesso');

      // Buscar dados do agente atual antes de entrar no sistema
      await fetchAgenteAtual();
      // Atualizar foto de perfil se vier do Google
      await atualizarFotoGoogle();

      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Erro ao ativar conta',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gradient-to-br from-background via-background to-background">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <FloatingOrb className="w-96 h-96 bg-primary/40 -top-48 -left-48" delay={0} />
        <FloatingOrb className="w-80 h-80 bg-secondary/40 top-1/3 -right-40" delay={2} />
        <FloatingOrb className="w-64 h-64 bg-primary/30 bottom-0 left-1/4" delay={4} />
        
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Left Panel - Desktop */}
      <div className="hidden lg:flex flex-col justify-center items-center flex-1 relative z-10 px-16">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-lg"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-8"
          >
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-20 object-contain"
            />
          </motion.div>

          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-20 h-20 rounded-2xl mb-6 flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--secondary) / 0.15))",
              border: "1px solid hsl(var(--primary) / 0.2)"
            }}
          >
            <Shield className="h-10 w-10 text-primary" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="font-heading text-5xl font-bold tracking-tight leading-tight mb-6"
          >
            <span className="text-foreground">Autenticação</span>
            <br />
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            >
              de dois fatores
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg text-muted-foreground leading-relaxed mb-10"
          >
            Para sua segurança, insira o código de 6 dígitos enviado
            para seu e-mail para confirmar sua identidade.
          </motion.p>

          {/* Info Cards */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <LockKeyhole className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">Proteção adicional</h3>
                <p className="text-xs text-muted-foreground">
                  Esta camada extra protege sua conta contra acessos não autorizados
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="flex items-start gap-4 p-4 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm mb-1">Código temporário</h3>
                <p className="text-xs text-muted-foreground">
                  O código expira em 2 minutos por sua segurança
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="relative">
            {/* Card glow effect */}
            <div 
              className="absolute -inset-1 rounded-3xl blur-xl opacity-20"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            />
            
            <div className="relative p-8 rounded-3xl border border-border/50 bg-card/80 backdrop-blur-xl shadow-2xl">
              {/* Mobile Logo */}
              <div className="lg:hidden flex justify-center mb-6">
                <img 
                  src={logoUrbnet} 
                  alt="UrbNet Logo" 
                  className="h-16 object-contain"
                />
              </div>

              {/* Header */}
              <div className="mb-8">
                <motion.button
                  whileHover={{ x: -3 }}
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </motion.button>
                <h2 className="font-heading text-2xl font-bold text-foreground">
                  {isPasswordStep ? 'Definir senha' : 'Verificar código'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {isPasswordStep
                    ? 'Crie sua senha usando os requisitos abaixo para ativar sua conta.'
                    : 'Digite o código de 6 dígitos enviado para seu e-mail'}
                </p>
              </div>

              {/* Form */}
              {step === 1 ? (
                <form onSubmit={mode === "activate" && !codeSent ? handleRequestCode : handleVerifyCode} className="space-y-6">
                  {/* Email Input for activate mode */}
                  {mode === "activate" && !codeSent && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.4 }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <label htmlFor="activateEmail" className="text-sm font-medium text-foreground">
                          E-mail *
                        </label>
                        <input
                          id="activateEmail"
                          type="email"
                          placeholder="seu@email.com"
                          value={activateEmail}
                          onChange={(e) => setActivateEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full px-4 py-3 rounded-xl border border-border/50 bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full h-12"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            Enviando...
                          </>
                        ) : (
                          'Enviar código'
                        )}
                      </Button>
                    </motion.div>
                  )}

                  {/* Code Input */}
                  {(mode !== "activate" || codeSent) && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.4 }}
                      className="space-y-4"
                    >
                      <div className="flex gap-2 justify-center">
                        {code.map((digit, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05, duration: 0.3 }}
                        >
                          <input
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={(e) => index === 0 && handlePaste(e)}
                            className="w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all duration-300 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                            style={{
                              borderColor: digit 
                                ? "hsl(var(--primary))" 
                                : "hsl(var(--border))",
                            }}
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Timer */}
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        Código expira em{" "}
                        <span className="font-semibold text-foreground">
                          {formatTime(timeLeft)}
                        </span>
                      </p>
                    </div>
                  </motion.div>
                  )}

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="relative w-full h-14 rounded-xl font-semibold text-base overflow-hidden group"
                      style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))",
                      }}
                    >
                      <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {isLoading ? (
                        <motion.div
                          className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <span className="relative flex items-center gap-2">
                          Verificar código
                          <ArrowRight className="h-5 w-5" />
                        </span>
                      )}
                    </Button>
                  </motion.div>

                  {/* Resend Code */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                    className="text-center"
                  >
                    <p className="text-sm text-muted-foreground">
                      Não recebeu o código?{" "}
                      {canResend ? (
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={isLoading}
                          className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Reenviar
                        </button>
                      ) : (
                        <span className="text-muted-foreground">
                          Reenviar em {formatTime(timeLeft)}
                        </span>
                      )}
                    </p>
                  </motion.div>
                </form>
              ) : (
                <form onSubmit={handleActivateAccount} className="space-y-6">
                  {/* Password Fields */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="space-y-4"
                  >
                    {/* Password */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Senha</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Digite sua senha"
                          value={formData.senha}
                          onChange={handlePasswordChange('senha')}
                          className="w-full h-12 pl-10 pr-10 rounded-xl border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                          disabled={isLoading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.senha && (
                        <p className="text-sm text-destructive">{errors.senha}</p>
                      )}
                      <div className="mt-2 rounded-lg border border-border/60 bg-surface-container p-3 space-y-2 text-xs text-muted-foreground">
                        <p className="font-medium text-foreground/80">A senha deve conter:</p>
                        <ul className="space-y-1.5">
                          {passwordRules.map((rule) => (
                            <motion.li
                              key={rule.label}
                              layout
                              initial={false}
                              className="flex items-center gap-2"
                              animate={{
                                color: rule.valid ? 'hsl(var(--foreground))' : 'hsl(var(--destructive))',
                                opacity: rule.valid ? 1 : 0.9,
                              }}
                              transition={{ duration: 0.25 }}
                            >
                              <motion.span
                                initial={false}
                                animate={{
                                  scale: rule.valid ? 1.05 : 1,
                                  color: rule.valid ? '#22c55e' : 'hsl(var(--destructive))',
                                }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                className="flex items-center justify-center"
                              >
                                {rule.valid ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : (
                                  <AlertCircle className="h-4 w-4" />
                                )}
                              </motion.span>
                              <motion.span
                                initial={false}
                                animate={{
                                  color: rule.valid ? 'hsl(var(--foreground))' : 'hsl(var(--destructive))',
                                }}
                                className="transition-colors"
                              >
                                {rule.label}
                              </motion.span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Confirmar Senha</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirme sua senha"
                          value={formData.confirmarSenha}
                          onChange={handlePasswordChange('confirmarSenha')}
                          className="w-full h-12 pl-10 pr-10 rounded-xl border-2 border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                          disabled={isLoading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.confirmarSenha && (
                        <p className="text-sm text-destructive">{errors.confirmarSenha}</p>
                      )}
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="relative w-full h-14 rounded-xl font-semibold text-base overflow-hidden group"
                      style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))",
                      }}
                    >
                      <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {isLoading ? (
                        <motion.div
                          className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <span className="relative flex items-center gap-2">
                          Ativar conta
                          <CheckCircle2 className="h-5 w-5" />
                        </span>
                      )}
                    </Button>
                  </motion.div>

                  {/* Back Button */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                    className="text-center"
                  >
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      disabled={isLoading}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Voltar
                    </button>
                  </motion.div>
                </form>
              )}

              {/* Footer */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="text-xs text-muted-foreground text-center mt-6"
              >
                Precisa de ajuda?{" "}
                <button className="text-primary hover:underline font-medium">
                  Entre em contato com o suporte
                </button>
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
